import { RefObject, useEffect } from 'react'

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  onlyFireOnce?: boolean
}

/**
 * Used for triggering a callback when an element is intersecting with the viewport
 * i.e. visible on the screen
 *
 * @param ref - Ref object of the element to observe
 * @param callback - Function to call when the element is intersecting
 * @param options - IntersectionObserver options
 */

const useIntersectionObserver = (
  ref: RefObject<Element>,
  callback: (entry: IntersectionObserverEntry) => void,
  options: UseIntersectionObserverOptions = {},
  dependencies: any[] = [],
): void => {
  useEffect(() => {
    const { onlyFireOnce = false, ...restOptions } = options

    const observerCallback = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver,
    ): void => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry)

          // If onlyFireOnce is true, disconnect after the first trigger
          if (onlyFireOnce) {
            observer.disconnect()
          }
        }
      })
    }

    const target = ref.current
    if (!target) {
      return
    }

    const observer = new IntersectionObserver(observerCallback, restOptions)

    observer.observe(target)

    // Clean-up function to disconnect observer
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect()
  }, [ref, ...dependencies])
}

export default useIntersectionObserver
